/**
 * Structured Work Specifications frequency helper.
 *
 * A work-spec row stores its frequency as a plain string (e.g. "3 days/week"),
 * which is rendered verbatim in the contract PDF. This module lets the editor
 * present a structured selector (recurrence-type/unit + count) while keeping the
 * stored value a string, so no PDF/storage changes are needed. Formatting mirrors
 * `summarizeRecurrence()` in utils/lineItemHelpers.ts.
 */

export type WorkSpecFrequencyKind =
  | 'none'
  | 'days_per_week'
  | 'days_per_month'
  | 'every_x_weeks'
  | 'every_x_months'
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'as_needed'
  | 'each_visit'
  | 'other'

export interface WorkSpecFrequencyValue {
  kind: WorkSpecFrequencyKind
  count: number // used only for numeric kinds
  custom: string // used only for kind === 'other'
}

/** Kinds that require an accompanying numeric count. */
export const NUMERIC_FREQUENCY_KINDS: WorkSpecFrequencyKind[] = [
  'days_per_week',
  'days_per_month',
  'every_x_weeks',
  'every_x_months',
]

/** Dropdown options, in display order. */
export const FREQUENCY_KIND_OPTIONS: { value: WorkSpecFrequencyKind; label: string }[] = [
  { value: 'none', label: '— (none)' },
  { value: 'days_per_week', label: 'Days per week' },
  { value: 'days_per_month', label: 'Days per month' },
  { value: 'every_x_weeks', label: 'Every X weeks' },
  { value: 'every_x_months', label: 'Every X months' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'as_needed', label: 'As needed' },
  { value: 'each_visit', label: 'Each visit' },
  { value: 'other', label: 'Other…' },
]

export function isNumericFrequencyKind(kind: WorkSpecFrequencyKind): boolean {
  return NUMERIC_FREQUENCY_KINDS.includes(kind)
}

/** Compose the structured value into the stored frequency string. */
export function composeFrequency(value: WorkSpecFrequencyValue): string {
  const n = Math.max(1, Math.floor(value.count || 1))
  switch (value.kind) {
    case 'days_per_week':
      return `${n} day${n === 1 ? '' : 's'}/week`
    case 'days_per_month':
      return `${n} day${n === 1 ? '' : 's'}/month`
    case 'every_x_weeks':
      return `every ${n} week${n === 1 ? '' : 's'}`
    case 'every_x_months':
      return `every ${n} month${n === 1 ? '' : 's'}`
    case 'daily':
      return 'Daily'
    case 'weekly':
      return 'Weekly'
    case 'bi_weekly':
      return 'Bi-weekly'
    case 'monthly':
      return 'Monthly'
    case 'as_needed':
      return 'As needed'
    case 'each_visit':
      return 'Each visit'
    case 'other':
      return (value.custom || '').trim()
    case 'none':
    default:
      return ''
  }
}

/**
 * Best-effort parse of an existing frequency string back into the structured
 * value so the selector can hydrate. Unrecognized non-empty strings map to
 * 'other' (preserving the raw text); empty strings map to 'none'.
 */
export function parseFrequency(raw: string | null | undefined): WorkSpecFrequencyValue {
  const s = (raw || '').trim()
  if (!s) return { kind: 'none', count: 1, custom: '' }

  let m: RegExpMatchArray | null
  if ((m = s.match(/^(\d+)\s*days?\s*\/\s*week$/i))) {
    return { kind: 'days_per_week', count: parseInt(m[1], 10) || 1, custom: '' }
  }
  if ((m = s.match(/^(\d+)\s*days?\s*\/\s*month$/i))) {
    return { kind: 'days_per_month', count: parseInt(m[1], 10) || 1, custom: '' }
  }
  if ((m = s.match(/^every\s+(\d+)\s*weeks?$/i))) {
    return { kind: 'every_x_weeks', count: parseInt(m[1], 10) || 1, custom: '' }
  }
  if ((m = s.match(/^every\s+(\d+)\s*months?$/i))) {
    return { kind: 'every_x_months', count: parseInt(m[1], 10) || 1, custom: '' }
  }

  const lc = s.toLowerCase()
  if (lc === 'as needed') return { kind: 'as_needed', count: 1, custom: '' }
  if (lc === 'each visit') return { kind: 'each_visit', count: 1, custom: '' }
  if (lc === 'daily') return { kind: 'daily', count: 1, custom: '' }
  if (lc === 'weekly') return { kind: 'weekly', count: 1, custom: '' }
  if (lc === 'bi-weekly' || lc === 'biweekly' || lc === 'bi weekly') {
    return { kind: 'bi_weekly', count: 1, custom: '' }
  }
  if (lc === 'monthly') return { kind: 'monthly', count: 1, custom: '' }

  return { kind: 'other', count: 1, custom: s }
}
